import ChatBox from '@src/components/pc/Claw/chat/Chatbox';
import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  type ComponentProps,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { ListStatus, ListStatusComponent } from './components/list-status';
import { MessageItem, SteamMessageItem } from './components/message-item';
import GuideTip from '@src/components/pc/Claw/chat/GuideTip';
import JoinGroupKimoModal from '@src/components/pc/Claw/chat/JoinGroupKimoModal';
import { ArrowContinueLineIcon } from '@kim/ui';
import { IMessageItem, ImRole, StreamStatus } from './type';
import { useChatListGuidePlaceholder } from './useChatListGuidePlaceholder';
import { useShareChat } from '@src/hooks/useShareChat';
import { parseClawAgentSessionKey } from '@src/hooks/useClawWs';
import { copyTextWithFallback } from '../../../utils/clipboard-copy';
import { Toast } from '@kim/ui';
export * from './utils';
import styles from './index.module.less';
import { message } from 'antd';

export type ChatListProps = {
  list: IMessageItem[];
  status: ListStatus;
  onRetry: () => void;
  hideHeader?: boolean;
  isShowPlaceholder?: boolean;
  agentChip?: ComponentProps<typeof ChatBox>['agentChip'];
  disabled?: ComponentProps<typeof ChatBox>['disabled'];
  onChatBoxClick?: ComponentProps<typeof ChatBox>['onClick'];
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onSendMessage: (message: string) => void;
  searchCollaborators?: ComponentProps<typeof ChatBox>['searchCollaborators'];
  chatBoxPlaceholder?: string;
  hideAttachmentUpload?: boolean;
  setScrollRef?: (ref: HTMLDivElement | null) => void;
  isShowStream?: boolean;
  shareMode?: boolean;
  onExitShare?: () => void;
  shareSessionKey?: string;
  streamInfo?: {
    streamStatus?: StreamStatus;
    message?: IMessageItem;
  };
};

export interface ChatListRef {
  getScrollEle: () => HTMLDivElement | null;
  clearEditor: () => void;
}

export { ListStatus, ListStatusComponent } from './components/list-status';
export { MessageItem } from './components/message-item';

export const ChatList = forwardRef<ChatListRef, ChatListProps>(
  function ChatList(
    {
      list,
      status,
      onRetry,
      agentChip,
      onScroll,
      hideHeader,
      streamInfo,
      isShowStream,
      shareMode,
      onExitShare,
      shareSessionKey,
      onSendMessage,
      isShowPlaceholder,
      searchCollaborators,
      chatBoxPlaceholder,
      hideAttachmentUpload,
      disabled = false,
      onChatBoxClick,
      setScrollRef,
    },
    ref,
  ) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const chatBoxRef = useRef<{ clearEditor: () => void } | null>(null);
    const suggestionsRef = useRef<HTMLDivElement | null>(null);
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(
      new Set(),
    );
    const { sharing, share } = useShareChat();
    const listIds = useMemo(
      () => list.map((item, idx) => `${item.id ?? idx}`),
      [list],
    );
    const streamId = '__stream__';
    const selectableIds = useMemo(
      () => (isShowStream ? [...listIds, streamId] : listIds),
      [listIds, isShowStream],
    );
    const isAllSelected =
      selectableIds.length > 0 && selectedIds.size === selectableIds.length;
    const mapShareRole = useCallback((role?: ImRole) => {
      if (role === ImRole.SELF) return 'user';
      if (role === ImRole.ASSISTANT) return 'assistant';
      return 'system';
    }, []);
    const isResponding = useMemo(() => {
      return [StreamStatus.READY, StreamStatus.STREAMING].includes(
        streamInfo?.streamStatus ?? StreamStatus.NONE,
      );
    }, [streamInfo?.streamStatus]);

    useImperativeHandle(
      ref,
      () => ({
        getScrollEle: () => scrollRef.current,
        clearEditor: () => chatBoxRef.current?.clearEditor(),
      }),
      [],
    );
    const updateScrollRef = useCallback(
      (el: HTMLDivElement | null) => {
        scrollRef.current = el;
        if (setScrollRef) {
          setScrollRef(el);
        }
      },
      [setScrollRef],
    );
    useEffect(() => {
      if (!shareMode) setSelectedIds(new Set());
    }, [shareMode]);
    const handleToggleSelect = useCallback((id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);
    const handleSelectAll = useCallback(() => {
      if (isAllSelected) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(selectableIds));
      }
    }, [isAllSelected, selectableIds]);
    const handleConfirmShare = useCallback(async () => {
      if (!shareSessionKey) {
        Toast({ message: '当前会话不可分享', type: 'warning' });
        return;
      }
      if (selectedIds.size === 0) {
        Toast({ message: '请先选择要分享的消息', type: 'warning' });
        return;
      }
      const selectedMsgs = [
        ...list.filter((item, idx) => selectedIds.has(`${item.id ?? idx}`)),
        ...(isShowStream && selectedIds.has(streamId)
          ? [streamInfo?.message ?? {}]
          : []),
      ];
      const messagePayloads: string[] = [];
      for (const msg of selectedMsgs) {
        const text = (msg.content ?? '').trim();
        if (!text) continue;
        messagePayloads.push(
          JSON.stringify({ role: mapShareRole(msg.role), content: text }),
        );
      }
      if (messagePayloads.length === 0) {
        Toast({ message: '所选消息没有可分享的内容', type: 'warning' });
        return;
      }
      const { agentId } = parseClawAgentSessionKey(shareSessionKey);
      try {
        const shareId = await share({
          marioAgentId: agentId ?? undefined,
          sessionKey: shareSessionKey.split(':').pop() ?? shareSessionKey,
          messages: messagePayloads,
        });
        const shareUrl = `${window.location.origin}/message-share/${shareId}`;
        const copyResult = await copyTextWithFallback(shareUrl);
        Toast({
          message: copyResult.success
            ? '分享链接已生成，快去分享吧'
            : `分享链接已生成`,
          type: 'success',
          buttonText: copyResult.success ? undefined : '请点击复制',
          buttonClick: () => copyTextWithFallback(shareUrl),
        });
        onExitShare?.();
      } catch {
        Toast({ message: '分享失败，请重试', type: 'error' });
      }
    }, [
      shareSessionKey,
      selectedIds,
      list,
      isShowStream,
      streamInfo?.message,
      mapShareRole,
      share,
      onExitShare,
    ]);

    const {
      isWbOrFl,
      onSelectItem,
      handleSuggestionClick,
      chatBoxPlaceholderResolved,
      chatBoxTemplate,
      chatBoxPrefilledText,
      handleClearTemplate,
      handleCloseJoinModal,
      onEditorEmptyChange,
      handleJoinGroupConfirm,
      handleJoinKimoConfirm,
      activeSuggestionItem,
      clearEditorNonce,
      joinGroupKimoModalVisible,
      isInJoingroup,
      kimoSkillStatus,
      kimoJoiningLoading,
      groupBotNumber,
      kimoBotNumber,
    } = useChatListGuidePlaceholder({
      suggestionsRef,
      onSendMessage,
      isShowPlaceholder,
      chatBoxPlaceholder,
    });

    return (
      <div className={styles.root}>
        {isShowPlaceholder ? (
          <div className={styles.placeholderArea}>
            <GuideTip
              agentType={agentChip?.agentType}
              onSelectItem={onSelectItem}
            />
          </div>
        ) : (
          <div
            className={styles.scrollContainer}
            onScroll={onScroll}
            ref={updateScrollRef}
          >
            <ListStatusComponent status={status} onRetry={onRetry} />
            {list.map((item, idx) => {
              const id = `${item.id ?? idx}`;
              const checked = selectedIds.has(id);
              return shareMode ? (
                <div
                  key={id}
                  className={`msg-select-row msg-select-row--${item.role ?? ImRole.ASSISTANT}`}
                  onClick={() => handleToggleSelect(id)}
                >
                  <span
                    className={`msg-select-radio${checked ? ' msg-select-radio--checked' : ''}`}
                  >
                    {checked && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 3.5L3.8 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <MessageItem message={item} hideHeader={hideHeader} />
                </div>
              ) : (
                <MessageItem key={id} message={item} hideHeader={hideHeader} />
              );
            })}
            {isShowStream &&
              (shareMode ? (
                <div
                  key={streamId}
                  className="msg-select-row msg-select-row--assistant"
                  onClick={() => handleToggleSelect(streamId)}
                >
                  <span
                    className={`msg-select-radio${
                      selectedIds.has(streamId)
                        ? ' msg-select-radio--checked'
                        : ''
                    }`}
                  >
                    {selectedIds.has(streamId) && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 3.5L3.8 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <SteamMessageItem
                    streamStatus={streamInfo?.streamStatus ?? StreamStatus.NONE}
                    message={streamInfo?.message ?? {}}
                    hideHeader={hideHeader}
                  />
                </div>
              ) : (
                <SteamMessageItem
                  key={streamId}
                  streamStatus={streamInfo?.streamStatus ?? StreamStatus.NONE}
                  message={streamInfo?.message ?? {}}
                  hideHeader={hideHeader}
                />
              ))}
          </div>
        )}
        <div
          className={styles.chatBoxContainer}
          style={shareMode ? { display: 'none' } : undefined}
        >
          <div className={styles.chatBoxStack}>
            {Boolean(
              isShowPlaceholder && activeSuggestionItem?.suggestions?.length,
            ) && (
              <div ref={suggestionsRef} className={styles.suggestions}>
                {activeSuggestionItem!.suggestions!.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    className={styles.suggestionItem}
                    onClick={() => handleSuggestionClick(item.value)}
                  >
                    <ArrowContinueLineIcon
                      className={styles.suggestionItemIcon}
                      aria-hidden
                      size={16}
                    />
                    <span className={styles.suggestionItemText}>
                      {item.content}
                    </span>
                  </button>
                ))}
              </div>
            )}
              <ChatBox
                ref={chatBoxRef}
                agentChip={agentChip}
                disabled={disabled}
                onClick={onChatBoxClick}
                isResponding={isResponding}
                hideAttachmentUpload={hideAttachmentUpload}
                className={styles.chatBox}
                placeholder={chatBoxPlaceholderResolved}
                template={chatBoxTemplate}
                prefilledText={chatBoxPrefilledText}
                clearEditorNonce={clearEditorNonce}
                onEditorEmptyChange={onEditorEmptyChange}
                onSubmit={onSendMessage}
                searchCollaborators={searchCollaborators}
                onClearTemplate={
                  isShowPlaceholder ? handleClearTemplate : undefined
                }
              />
          </div>
        </div>
        {shareMode && (
          <div className="claw-share-bar">
            <div className="claw-share-bar__inner">
              <div className="claw-share-bar__left" onClick={handleSelectAll}>
                <span
                  className={`msg-select-radio${isAllSelected ? ' msg-select-radio--checked' : ''}`}
                >
                  {isAllSelected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 3.5L3.8 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className="claw-share-bar__label">全选</span>
              </div>
              <div className="claw-share-bar__right">
                <button
                  type="button"
                  className="claw-share-bar__btn claw-share-bar__btn--cancel"
                  onClick={onExitShare}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="claw-share-bar__btn claw-share-bar__btn--confirm"
                  onClick={handleConfirmShare}
                  disabled={sharing}
                >
                  {sharing ? (
                    <span className="claw-share-btn-spinner" />
                  ) : (
                    '分享'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <JoinGroupKimoModal
          isWbOrFl={isWbOrFl}
          groupBotNumber={groupBotNumber}
          kimoBotNumber={kimoBotNumber}
          kimoLoading={kimoJoiningLoading}
          visible={joinGroupKimoModalVisible}
          joinGroupText={isInJoingroup ? '已加入 查看群聊' : '立即加入群聊'}
          joinKimoText={
            kimoSkillStatus ? '已加入 查看社区' : '立即加入 Kimobook'
          }
          onClose={handleCloseJoinModal}
          onJoinGroup={handleJoinGroupConfirm}
          onJoinKimo={handleJoinKimoConfirm}
        />
      </div>
    );
  },
);
